// screens-furrow.tsx - Marketing: Furrow game page
// Standard site chrome and theme throughout, except the hero header, which
// stays a fixed dark scrim over the game's key art (independent of theme,
// like any photographic hero) - same treatment as screens-ninelives.tsx.
//
// The CTA links to /play/furrow (the platform's own play-landing route),
// not a bare href to furrow.sstk.studio the way screens-sunder.tsx and
// screens-ninelives.tsx link straight to their own domains. Those two games
// each ship their own in-client login screen, so a bare landing works.
// Furrow deliberately doesn't (docs/production/decisions.md in sstk-furrow,
// "Cognito player bridge": "one login, flag which apps they use") - a
// visitor has to arrive via /play/furrow so the platform can mint them a
// player-link exchange code first; a bare link would strand them on
// furrow.sstk.studio with no way to sign in at all.
// Exports: ScreenFurrow
import { Glyph, MarketingFooter, MarketingNav, PlayCta } from '../components/ui-shared';
import { ImageSlot } from '../components/ImageSlot';
import { keyArt, mediaSrc, useOriginalMedia } from '../showcase/media';
import { TrailerPlayer } from '../showcase/screens-game';

function FurrowHero() {
  const media = useOriginalMedia('furrow');
  const art = keyArt(media);
  return (
    <header style={{ position: 'relative', height: 680, display: 'flex', flexDirection: 'column', background: '#1c1c1c' }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <ImageSlot shape="rect" placeholder="Furrow hero key art"
          src={mediaSrc(art?.url) ?? undefined}
          alt={art?.alt ?? ""}
          style={{ width: '100%', height: '100%', display: 'block' }} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(28,28,28,0.5) 0%, rgba(28,28,28,0.1) 40%, rgba(28,28,28,0.95) 90%)' }} />
      </div>
      <div style={{ position: 'relative', marginTop: 'auto', maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', padding: '0 28px 72px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <span className="ss-eyebrow" style={{ color: 'var(--ss-bone)' }}>A Soulstack Original · Cosy Co-op Farming</span>
        <h1 className="ss-display" style={{
          fontSize: 'clamp(3rem, 18vw, 7.25rem)', lineHeight: 0.9, letterSpacing: '0.02em', textTransform: 'uppercase', color: '#ece9e3',
          textShadow: '0 0 90px rgba(200,210,220,0.12), 0 2px 8px rgba(0,0,0,0.7)',
        }}>Furrow</h1>
        <p style={{ color: 'rgba(200,202,206,0.75)', fontSize: 17.5, maxWidth: 480, lineHeight: 1.65, textWrap: 'pretty' }}>
          A shared valley for one to four cats. Farm, forage, fish and build
          alongside friends, on foot with tools in paw or down on all fours
          to run.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
          <PlayCta className="ss-btn ss-btn--bone ss-btn--lg" to="/play/furrow">Play in browser</PlayCta>
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(160,164,170,0.5)' }}>
          Free to play · In the browser or installed · 1 to 4 players
        </span>
      </div>
    </header>
  );
}

export const FURROW_PILLARS = [
  { k: '4', title: 'One valley, up to four cats', copy: 'Farm side by side in a real shared valley. Plots, herds and workshops that everyone in the session can see and use.' },
  { k: '2', title: 'Two postures, one cat', copy: 'Bipedal for tools, all fours for speed. Every task in Furrow reads that trade-off: hold the basket, or run for it.' },
  { k: '1', title: 'One login', copy: 'Sign in once on Soulstack Studio and connect Furrow from your account. No separate password to make or remember.' },
];

function FurrowPillars() {
  const items = FURROW_PILLARS;
  return (
    <section className="mk-section">
      <div className="mk-inner">
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {items.map((it) => (
            <article key={it.title} className="ss-card" style={{ padding: '34px 32px 36px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span className="ss-display" style={{ fontSize: 44, color: 'var(--ss-accent)' }}>{it.k}</span>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--ss-text)' }}>{it.title}</h3>
              <p style={{ color: 'var(--ss-text-2)', fontSize: 14.5, textWrap: 'pretty' }}>{it.copy}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FurrowMedia() {
  const media = useOriginalMedia('furrow');
  const shots = media?.screenshots ?? [];
  return (
    <section className="mk-section" style={{ paddingTop: 0 }}>
      <div className="mk-inner">
        <div className="mk-section-head">
          <span className="ss-eyebrow ss-eyebrow--dim">Media</span>
          <h2 className="ss-display" style={{ fontSize: 40 }}>Around Hearthollow Fields</h2>
        </div>
        {media?.trailer && (
          <div style={{ marginBottom: 16 }}>
            <TrailerPlayer media={media} name="Furrow" />
          </div>
        )}
        <div className="ss-grid-collapse mk-media-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gridTemplateRows: '300px 300px', gap: 16 }}>
          <ImageSlot shape="rounded" radius={14} placeholder="Gameplay still: wide" src={mediaSrc(shots[0]?.url) ?? undefined} alt={shots[0]?.alt ?? ""} style={{ gridRow: '1 / 3', width: '100%', height: '100%', display: 'block' }} />
          <ImageSlot shape="rounded" radius={14} placeholder="Gameplay still" src={mediaSrc(shots[1]?.url) ?? undefined} alt={shots[1]?.alt ?? ""} style={{ width: '100%', height: '100%', display: 'block' }} />
          <ImageSlot shape="rounded" radius={14} placeholder="Gameplay still" src={mediaSrc(shots[2]?.url) ?? undefined} alt={shots[2]?.alt ?? ""} style={{ width: '100%', height: '100%', display: 'block' }} />
        </div>
      </div>
    </section>
  );
}

function FurrowCta() {
  return (
    <section style={{ padding: '110px 28px', textAlign: 'center', borderTop: '1px solid var(--ss-border-soft)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
        <h2 className="ss-display" style={{ fontSize: 48, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ss-text)' }}>Bring the harvest in.</h2>
        <PlayCta className="ss-btn ss-btn--bone ss-btn--lg" to="/play/furrow">Play Furrow now</PlayCta>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, color: 'var(--ss-text-3)' }}>
          <Glyph size={16} />
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Built and hosted on Soulstack Studio</span>
        </div>
      </div>
    </section>
  );
}

export function ScreenFurrow() {
  return (
    <div className="ss-screen">
      <MarketingNav active="Showcase" />
      <FurrowHero />
      <FurrowPillars />
      <FurrowMedia />
      <FurrowCta />
      <MarketingFooter />
    </div>
  );
}
